import React, { useState } from "react";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  useTheme,
  Card,
  Tooltip,
} from "@mui/material";
import {
  Add,
  MoreVert,
} from "@mui/icons-material";

import { Menu, MenuItem } from "@mui/material";
import axios from "axios";
import axiosInstance from "../../axios/axios";
import { toast } from "react-toastify";
import Skeleton from "@mui/material/Skeleton";
import Region_World_map_img from "../../assets/baseVersion/Region_World_map_img.svg";
import useRolePermissions from "../../shared/hooks/useRolePermissions";
import { REGION_ROUTE } from "../../shared/constants/constant-data";

interface RegionManagerProps {
  levels: any;
  onConfigureBack: () => void;
  preloadedRegionData: Array<any>;
  isDisabled?: boolean;
}

const RegionManager: React.FC<RegionManagerProps> = ({
  levels,
  onConfigureBack,
  preloadedRegionData,
  isDisabled = false,
}) => {
  const [regionData, setRegionData] = useState<Array<any>>(
    preloadedRegionData ?? []
  );
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuCell, setMenuCell] = useState<{
    rowIndex: number;
    colIndex: number;
  } | null>(null);
  const open = Boolean(menuAnchorEl);
  const [openDialog, setOpenDialog] = useState(false);
  const [pendingDeleteCell, setPendingDeleteCell] = useState<any | null>(null);
  const [columnLoadingState, setColumnLoadingState] = useState<boolean[]>([]);
  const { hasPermissions } = useRolePermissions();
  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    rowIndex: number,
    colIndex: number
  ) => {
    if (isDisabled) return;
    setMenuAnchorEl(event.currentTarget);
    setMenuCell({ rowIndex, colIndex });
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuCell(null);
  };

  const handleEdit = () => {
    if (menuCell) {
      const { rowIndex, colIndex } = menuCell;
      setRegionData((prev) => {
        const newData = [...prev];
        const column = [...newData[colIndex]];
        if (column[rowIndex]) {
          column[rowIndex].editable = true;
        }
        newData[colIndex] = column;
        return newData;
      });
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (menuCell) {
      const { rowIndex, colIndex } = menuCell;
      const cell = regionData[colIndex]?.[rowIndex];
      if (cell) {
        setPendingDeleteCell({ rowIndex, colIndex, name: cell.name });
        setOpenDialog(true);
      }
    }
    handleMenuClose();
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setPendingDeleteCell(null);
  };

  const handleConfirmDelete = async () => {
    if (pendingDeleteCell) {
      const { rowIndex, colIndex } = pendingDeleteCell;
      const cell = regionData[colIndex]?.[rowIndex];

      if (!cell?.id) {
        console.error("No valid cell to delete.");
        handleCloseDialog();
        return;
      }

      try {
        const payload = {
          action: "delete",
          region: {
            id: cell.id,
          },
        };

        const response = await axiosInstance.post(
          "/authentication/region-data",
          payload
        );
        console.log("Delete successful:", response.data);
        if (response.data.status === 200) {
          toast.success(response.data.message);
          setRegionData((prev) => {
            const newData = [...prev];
            const column = [...newData[colIndex]];
            column.splice(rowIndex, 1);
            newData[colIndex] = column;
            return newData;
          });
        } else {
          toast.error(response.data.message);
        }
      } catch (error: any) {
        toast.error(
          error.response?.data?.message ||
            "Failed to delete the level. Please try again."
        );
      }
    }

    handleCloseDialog();
  };

  const hasDataInColumn = (colIndex: number): boolean => {
    return (
      regionData[colIndex]?.some(
        (cell: any) => cell && cell.name && cell.name !== ""
      ) || false
    );
  };

  const handleAddColumn = (colIndex: number) => {
    if (isDisabled) return;
    setRegionData((prev) => {
      const newRegionData = [...prev];
      if (!newRegionData[colIndex]) newRegionData[colIndex] = [];
      newRegionData[colIndex].push({ name: "", editable: true, new: true });
      return newRegionData;
    });
  };

  // Validate region name: alphanumeric and spaces only, max 40 chars
  const isValidRegionName = (value: string): boolean => {
    const trimmed = value.trim();
    // Alphanumeric and spaces only, min 2, max 40 characters
    const alphanumericRegex = /^[a-zA-Z0-9 ]+$/;
    return trimmed.length >= 2 && trimmed.length <= 40 && alphanumericRegex.test(trimmed);
  };

  const handleChange = (
    rowIndex: number,
    colIndex: number,
    newValue: string
  ) => {
    // Filter to allow only alphanumeric characters and spaces, max 40 chars
    const filteredValue = newValue
      .replace(/[^a-zA-Z0-9 ]/g, '') // Remove non-alphanumeric except spaces
      .slice(0, 40); // Enforce max 40 characters
    
    setRegionData((prev) => {
      const newData = [...prev];
      const column = [...newData[colIndex]];
      column[rowIndex] = { ...column[rowIndex], name: filteredValue };
      newData[colIndex] = column;
      return newData;
    });
  };

  const handleSave = async (rowIndex: number, colIndex: number) => {
    if (isDisabled) return;
    const cell = regionData[colIndex]?.[rowIndex];
    console.log("cell", cell);

    if (!cell || !cell.name) return;

    const isNew = cell.new === true;
    const levelMeta = levels[colIndex];

    const payload = isNew
      ? {
          action: "add",
          region: {
            parent_id: levelMeta.parent_id,
            name: cell.name,
            region_level_id: levelMeta.id,
          },
        }
      : {
          action: "modify",
          region: {
            id: cell.id,
            name: cell.name,
          },
        };

    try {
      const response = await axiosInstance.post(
        "/authentication/region-data",
        payload
      );
      console.log("Saved successfully:", response.data);
      if (response.data.status == 200) {
        toast.success(response.data.message);
        let tempcolumn: any = null;
        setRegionData((prev) => {
          const newData = [...prev];
          const column = [...newData[colIndex]];
          if (column[rowIndex]) {
            column[rowIndex] = {
              ...column[rowIndex],
              editable: false,
              ...(isNew ? { new: false, id: response.data?.data?.id } : {}),
            };
          }
          newData[colIndex] = column;
          tempcolumn = column[rowIndex];
          tempcolumn.id = response.data?.data?.id;
          return newData;
        });
        handleCellClick(rowIndex, colIndex, tempcolumn);
      }

      setRegionData((prev) => {
        const newData = [...prev];
        const column = [...newData[colIndex]];
        if (column[rowIndex]) {
          column[rowIndex] = {
            ...column[rowIndex],
            editable: false,
            ...(isNew ? { new: false, id: response.data?.data?.id } : {}),
          };
        }
        newData[colIndex] = column;
        return newData;
      });
    } catch (error: any) {
      console.error("Error saving region data:", error);
      
      toast.error(error.response?.data?.message||"Failed to save region data. Please try again.");
    }
  };

  const handleCellClick = async (
    rowIndex: number,
    colIndex: number,
    column: any = null
  ) => {
    if (isDisabled) return;
    const clickedCell = regionData[colIndex]?.[rowIndex];
    if (column != null) {
      clickedCell.editable = false;
      if (clickedCell.name == column.name) {
        clickedCell.id = column.id;
      }
    }
    if (!clickedCell || clickedCell.editable) return;

    setSelectedRowIndex(rowIndex);

    const maxLevel = levels.length;
    const baseUrl = "/authentication/list-region-data";
    let parentId = clickedCell.id ?? column.id;

    const updatedRegionData = [...regionData.slice(0, colIndex + 1)];
    const loadingFlags = [...columnLoadingState];

    for (let nextCol = colIndex + 1; nextCol < maxLevel; nextCol++) {
      try {
        levels[nextCol].parent_id = parentId;
        loadingFlags[nextCol] = true;
        setColumnLoadingState([...loadingFlags]);

        const response = await axiosInstance.get(
          `${baseUrl}/${levels[nextCol].id}/${parentId}`
        );
        const nextLevelData = response.data.data || [];

        updatedRegionData[nextCol] = nextLevelData.map((item: any) => ({
          ...item,
          editable: false,
        }));

        loadingFlags[nextCol] = false;
        setColumnLoadingState([...loadingFlags]);

        if (nextLevelData.length === 0) break;

        parentId = nextLevelData[0].id;
      } catch (error) {
        console.error(`Failed to fetch data for level ${nextCol}:`, error);
        loadingFlags[nextCol] = false;
        setColumnLoadingState([...loadingFlags]);
        break;
      }
    }

    setRegionData(updatedRegionData);

    if (colIndex === maxLevel - 1) {
      try {
        await axios.post("/authentication/final-api-action", {
          id: clickedCell.id,
        });
        console.log("Final API call successful.");
      } catch (error) {
        console.error("Final API call failed:", error);
      }
    }
  };

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  return (
    <Card
      sx={{
        height: "calc(104vh - 110px)",
        display: "flex",
        flexDirection: "column",
        backgroundColor: isDarkMode ? "#1e1e1e" : "#fff",
        backgroundImage: `url(${Region_World_map_img})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center bottom",
        backgroundSize: "70%",
        borderRadius: 2,
        position: "relative",
        opacity: isDisabled ? 0.5 : 1,
        pointerEvents: isDisabled ? "none" : "auto",
        transition: "opacity 0.3s ease",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        p={2}
        sx={{ pointerEvents: "auto" }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: "bolder", fontSize: "18px" }}
          >
            Define your Organization's region structure (Edit Mode)
          </Typography>
          <Typography variant="body2" sx={{ color: "grey" }}>
            Region name must be 2-40 alphanumeric characters (A-Z, a-z, 0-9, spaces only)
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            onClick={onConfigureBack}
            sx={{ 
              color: isDarkMode ? "#fff" : "#3A2AdA",
              pointerEvents: "auto",
            }}
          >
            Configure Level
          </Button>
        </Box>
      </Box>
      {levels.length > 0 &&
        levels.some(
          (level: any) => level.name && level.name.trim().length >= 3
        ) && (
          <TableContainer sx={{ flexGrow: 1, overflow: "auto" }}>
            <Table sx={{ minWidth: 650, tableLayout: "fixed"  }} aria-label="region table">
              <TableHead>
                <TableRow
                  sx={{
                    height: 24,
                    "& th": {
                      fontWeight: "bold",
                      border: "1px solid rgba(224, 224, 224, 1)",
                      borderBottom: "1px solid rgba(224, 224, 224, 1)",
                    },
                  }}
                >
                  {[...levels]
                    .sort((a, b) => a.level - b.level)
                    .map((level: any, colIndex: any) => (
                      <TableCell key={colIndex} sx={{ width: 220 }}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            {level.name}
                          </Box>
                          {hasPermissions(REGION_ROUTE.MODIFY_REGION_DATA) && (
                            <Box display="flex" alignItems="center" gap={1}>
                            {(colIndex === 0 ||
                              hasDataInColumn(colIndex - 1)) && (
                              <IconButton
                                size="small"
                                onClick={() => handleAddColumn(colIndex)}
                                disabled={isDisabled}
                              >
                                <Add fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                          )}
                        </Box>
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody sx={{ p: 2 }}>
                {regionData.length > 0 ? (
                  Array.from({
                    length: Math.max(...regionData.map((col) => col.length)),
                  }).map((_, rowIndex) => (
                    <TableRow
                      key={rowIndex}
                      sx={{
                        height: 60,
                        ...(rowIndex === selectedRowIndex && {
                          backgroundColor: isDarkMode
                            ? "rgba(103, 58, 183, 0.08)"
                            : "#EFEDFF",
                        }),
                      }}
                    >
                      {regionData.map((column, colIndex) => {
                        const isLoading = columnLoadingState?.[colIndex];

                        if (isLoading) {
                          return (
                            <TableCell
                              key={colIndex}
                              sx={{
                                border: "none",
                                backgroundColor: isDarkMode ? "black" :"white"
                                  
                              }}
                            >
                              <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                height={60}
                              >
                                <Skeleton
                                  variant="rounded"
                                  width="100%"
                                  height={54}
                                  animation="wave"
                                  sx={{ borderRadius: 1 }}
                                />
                              </Box>
                            </TableCell>
                          );
                        }

                        const cell = column[rowIndex];
                        if (!cell)
                          return (
                            <TableCell key={colIndex} sx={{ border: "none" }} />
                          );

                        return (
                          <TableCell
                            key={colIndex}
                            sx={{
                              width: 220,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              cursor: isDisabled ? "not-allowed" : "pointer",
                            }}
                            onClick={() => handleCellClick(rowIndex, colIndex)}
                          >
                            {cell.editable ? (
                              <Tooltip title={cell.name || 'Enter region name'} placement="top" arrow>
                                <TextField
                                  size="medium"
                                  value={cell.name}
                                  autoFocus={cell.new}
                                  fullWidth
                                  variant="outlined"
                                  sx={{
                                    backgroundColor: isDarkMode ? "#1e1e1e" : "#fff",
                                   
                                  }}
                                  onBlur={() => {
                                    if (
                                      cell.new &&
                                      (!cell.name || cell.name.trim() === "")
                                    ) {
                                      setRegionData((prev) => {
                                        const updated = [...prev];
                                        const column = [...updated[colIndex]];
                                        column.splice(rowIndex, 1);
                                        updated[colIndex] = column;
                                        return updated;
                                      });
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (
                                      e.key === "Enter" &&
                                      isValidRegionName(cell.name)
                                    ) {
                                      handleSave(rowIndex, colIndex);
                                    }
                                  }}
                                  onChange={(e) =>
                                    handleChange(
                                      rowIndex,
                                      colIndex,
                                      e.target.value
                                    )
                                  }
                                  InputProps={{
                                    endAdornment: (
                                      <Button
                                        onClick={() =>
                                          handleSave(rowIndex, colIndex)
                                        }
                                        size="small"
                                        disabled={!isValidRegionName(cell.name) || isDisabled}
                                      >
                                        Save
                                      </Button>
                                    ),
                                  }}
                                  disabled={isDisabled}
                                />
                              </Tooltip>
                            ) : (
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{padding:'10px',
                                  backgroundColor: isDarkMode ? "#1e1e1e" : "#fff",
                                }}
                              >
                                {cell.name && cell.name.length > 100 ? (
                                  <Tooltip title={cell.name} placement="top" arrow>
                                    <span style={{
                                      display: "inline-block",
                                      maxWidth: 220,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      verticalAlign: "bottom",
                                    }}>{cell.name}</span>
                                  </Tooltip>
                                ) : (
                                  cell.name
                                )}
                                {hasPermissions(REGION_ROUTE.MODIFY_REGION_DATA) && (
                                  <IconButton
                                  size="small"
                                  onClick={(e) =>
                                    handleMenuClick(e, rowIndex, colIndex)
                                  }
                                  disabled={isDisabled}
                                >
                                  <MoreVert fontSize="small" />
                                </IconButton>
                                )}
                              </Box>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={levels.length}
                      align="center"
                      sx={{
                        height: 200,
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      <Typography variant="body2" color="textSecondary">
                        No region data available.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: 2,
        }}
      >
        {/* <img src={Region_World_map_img} alt="" style={{ width: "70%" }} /> */}
      </Box>
      <Dialog open={openDialog} onClose={handleCloseDialog}  sx={{
            "& .MuiDialog-paper": {
              position: "absolute",
              top: "50%",
              left: "50%",
              width: "400px",
              height: "250px",
              borderRadius: "4px",
              transform: "translate(-50%, -50%)",
              // padding: "10px 10px 0",
              padding:"3px",
              boxSizing: "border-box",
              overflow: "hidden", 
            },
          }}>
        <DialogTitle sx={{ textAlign: "center", fontWeight: 600 }}>
          {`Are you sure you want to delete this `}
          <Box
            component="span"
            sx={{
              display: "inline-block",
              maxWidth: "320px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              verticalAlign: "bottom",
            }}
            title={pendingDeleteCell?.name}
          >
            {pendingDeleteCell?.name}
          </Box>
          {`?`}
          <Typography variant="body2" sx={{mt:2,fontSize:"15px"}}>
            {" "}
            If deleted, rest of the sub regions will be removed along with it.
          </Typography>
        </DialogTitle>
        <DialogActions sx={{ justifyContent: "center",p:3 }}>
          <Button
            variant="outlined"
            onClick={handleCloseDialog}
            sx={{ color: "#6c63ff" ,width:130}}
            
          >
            No
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDelete}
            sx={{ backgroundColor: "#ff4d4f", color: "#fff",width:130 }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Menu anchorEl={menuAnchorEl} open={open} onClose={handleMenuClose}>
        <MenuItem onClick={handleEdit}>Edit</MenuItem>
        <MenuItem onClick={handleDelete}>Delete</MenuItem>
      </Menu>
    </Card>
  );
};

export default RegionManager;
